import axios from 'axios'
import Mango from './mango'
import { useStore } from '../composables/store'
import { loadOrFallback } from './pageLoad'
import { defaultSections, rowQuery } from '../../../config/helpers/homePage/sections.js'

// The home page's layout and row data, kept so the page renders complete on its
// first frame however a shopper reaches it:
//  - Landing on "/": the storefront server already put it in the HTML
//    (window.homePage, see front/public/index.js).
//  - Landing anywhere else: it is fetched in the background once the browser is
//    idle, so going to the home page later uses data that is already here.
//  - Either way, the home route waits for it before switching pages (home.vue's
//    beforeRouteEnter), so the page never fills in after it appears.
// Older than MAX_AGE, it is fetched again, so schedules and admin saves show up.

const MAX_AGE = 5 * 60 * 1000

let cache = null // { at, promise, sections, retry }

async function fetchHomePage() {
	let sections = null
	try {
		const { data } = await axios.get(`${useStore().api}/controllers/homePage`, { timeout: 5000 })
		if (data?.success && Array.isArray(data.sections)) sections = data.sections
	} catch (error) {
		console.error('Home page layout request failed:', error?.message || error)
	}
	// Without the layout the store still shows the built-in sections, but that
	// fallback is not kept: the next visit asks again.
	const found = !!sections
	if (!found) sections = defaultSections()

	await Promise.all(
		sections.map(async (section) => {
			const row = rowQuery(section)
			if (!row) return
			const items = await loadOrFallback(() => Mango[row.collection](row.query), null)
			if (Array.isArray(items)) section.items = items
		}),
	)
	return { sections, found }
}

function remember(promise) {
	const entry = { at: Date.now(), promise: null, sections: null }
	entry.promise = promise.then(({ sections, found }) => {
		entry.sections = sections
		entry.retry = !found
		return sections
	})
	cache = entry
	return entry.promise
}

const recent = () => cache && Date.now() - cache.at < MAX_AGE
const fresh = () => recent() && !cache.retry

// The home page data, from what is already here or fetched now. Never rejects.
export function loadHomePage() {
	return fresh() ? cache.promise : remember(fetchHomePage())
}

// The home page data only if it has already arrived, for a synchronous first render.
export function readyHomePage() {
	return recent() ? cache.sections : null
}

// Called once at startup: keep what the server sent, or fetch it when idle.
export function startHomePage() {
	// Set synchronously: the first render reads it before any promise settles.
	if (Array.isArray(window.homePage)) cache = { at: Date.now(), sections: window.homePage, promise: Promise.resolve(window.homePage) }
	window.homePage = null
	if (cache) return
	const whenIdle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2000))
	whenIdle(() => loadHomePage(), { timeout: 5000 })
}
